import { TextField } from "@dash/form";
import { DocsSelectDialog, type AnyDocSelectOption } from "@dash/select";
import type { FC } from "react";
import { useMemo } from "react";

import { isDocOfType } from "@megaron/docs-contracts";
import { ResourceId } from "@megaron/resource";
import type { Uuid } from "@megaron/uuid";

import { SupportSelect, type SupportSelectOption } from "../shared/SupportSelect";
import { supportRequiredFieldLabel } from "../shared/supportRequiredFieldLabel";
import type { SupportCreateTicketContactOption } from "./supportTicketReasonCategories";
import { reporterFieldsFromCustomerDoc, reporterFieldsFromContact } from "./supportReporterName";
import { supportFormFieldCss } from "./supportCreateTicketFormStyles";

type Props = {
  reporterFirstName: string;
  setReporterFirstName: (value: string) => void;
  reporterLastName: string;
  setReporterLastName: (value: string) => void;
  reporterEmail: string;
  setReporterEmail: (value: string) => void;
  selectedCustomerUuid: Uuid | undefined;
  setSelectedCustomerUuid: (value: Uuid | undefined) => void;
  contactOptions: SupportCreateTicketContactOption[];
  selectedContact: string;
  onContactChange: (value: string) => void;
  hasCustomerContext: boolean;
};

export const SupportCreateTicketReporterField: FC<Props> = ({
  reporterFirstName,
  setReporterFirstName,
  reporterLastName,
  setReporterLastName,
  reporterEmail,
  setReporterEmail,
  selectedCustomerUuid,
  setSelectedCustomerUuid,
  contactOptions,
  selectedContact,
  onContactChange,
  hasCustomerContext,
}) => {
  const contactSelectOptions = useMemo<SupportSelectOption<string>[]>(
    () => contactOptions.map((option) => ({ value: option.value, label: option.label })),
    [contactOptions],
  );

  const initiallySelectedDocIds = useMemo(
    () => (selectedCustomerUuid ? [ResourceId("customer", selectedCustomerUuid)] : []),
    [selectedCustomerUuid],
  );

  const applyCustomerDoc = (option: AnyDocSelectOption | null | undefined) => {
    if (!option || !isDocOfType("customer")(option)) {
      setSelectedCustomerUuid(undefined);
      return;
    }
    setSelectedCustomerUuid(option.uuid);
    const fields = reporterFieldsFromCustomerDoc(option);
    setReporterFirstName(fields.firstName);
    setReporterLastName(fields.lastName);
    setReporterEmail(fields.email);
  };

  const handleContactChange = (value: string) => {
    onContactChange(value);
    const selected = contactOptions.find((option) => option.value === value);
    if (!selected) return;
    const fields = reporterFieldsFromContact(selected);
    setReporterFirstName(fields.firstName);
    setReporterLastName(fields.lastName);
    setReporterEmail(fields.email);
  };

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      <DocsSelectDialog
        docType={["customer"]}
        variant="single-select"
        label={supportRequiredFieldLabel("Zgłaszający")}
        initiallySelectedDocIds={initiallySelectedDocIds}
        onSelect={applyCustomerDoc}
      />
      {hasCustomerContext && contactOptions.length > 0 ? (
        <SupportSelect<string>
          variant="form"
          label="Kontakt klienta"
          placeholder="Wybierz z kontaktów klienta..."
          value={selectedContact || null}
          options={contactSelectOptions}
          onChange={(value) => handleContactChange(value ?? "")}
        />
      ) : null}
      <div css={{ display: "flex", gap: "0.75rem", alignItems: "flex-end" }}>
        <TextField
          label="Imię"
          value={reporterFirstName}
          onChange={setReporterFirstName}
          css={[supportFormFieldCss, { flex: 1 }]}
        />
        <TextField
          label="Nazwisko"
          value={reporterLastName}
          onChange={setReporterLastName}
          css={[supportFormFieldCss, { flex: 1 }]}
        />
      </div>
      <TextField
        label="Email"
        value={reporterEmail}
        onChange={setReporterEmail}
        css={supportFormFieldCss}
      />
    </div>
  );
};
